import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loading from '../components/Loading';
import './InstagramStories.css';

const InstagramStories = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const instagramId = searchParams.get('instagram_id');

  const [loading, setLoading] = useState(true);
  const [stories, setStories] = useState([]);
  const [stats, setStats] = useState({});
  const [account, setAccount] = useState({});
  const [includeExpired, setIncludeExpired] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!instagramId) {
      setError('No se proporcionó el ID de Instagram');
      setLoading(false);
      return;
    }
    loadStories();
  }, [instagramId, includeExpired]);

  const loadStories = async () => {
    setLoading(true);
    setError(null);
    try {
      // Construir la URL del backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      const response = await axios.get(`${backendUrl}/instagram/api/stories/`, {
        params: {
          instagram_id: instagramId,
          include_expired: includeExpired,
          limit: 50
        },
        withCredentials: true
      });
      setStories(response.data.stories);
      setStats(response.data.stats);
      setAccount(response.data.account);
    } catch (err) {
      console.error('Error loading stories:', err);
      setError(err.response?.data?.error || 'Error al cargar las stories');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return 'Expirada';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `Expira en ${hours}h ${minutes}m`;
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="stories-page">
        <div className="error-container">
          <h2>❌ Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn-back">
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stories-page">
      {/* Header */}
      <div className="page-header">
        <button onClick={() => navigate(-1)} className="btn-back-header">
          ← Volver
        </button>
        <div className="header-content">
          <div className="account-info">
            {account.profile_picture && (
              <img
                src={account.profile_picture}
                alt={account.username}
                className="account-avatar"
              />
            )}
            <div>
              <h1 className="page-title">📸 Stories de Instagram</h1>
              <p className="account-username">@{account.username}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-section">
        <div className="stat-card">
          <span className="stat-value">{stats.total_stories || 0}</span>
          <span className="stat-label">📸 Total Stories</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.active_stories || 0}</span>
          <span className="stat-label">✅ Activas</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{formatNumber(stats.total_impressions || 0)}</span>
          <span className="stat-label">👁️ Impresiones</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{formatNumber(stats.total_reach || 0)}</span>
          <span className="stat-label">📡 Alcance</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{formatNumber(stats.total_replies || 0)}</span>
          <span className="stat-label">💬 Respuestas</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{formatNumber(stats.avg_impressions || 0)}</span>
          <span className="stat-label">📊 Promedio Impresiones</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
          />
          <span>Incluir stories expiradas</span>
        </label>
      </div>

      {/* Stories Grid */}
      {stories.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No hay stories disponibles</h3>
          <p>
            {includeExpired
              ? 'No se han capturado stories para esta cuenta'
              : 'No hay stories activas. Activa el filtro para ver stories expiradas.'}
          </p>
        </div>
      ) : (
        <div className="stories-grid">
          {stories.map((story) => (
            <div
              key={story.id}
              className={`story-card ${story.is_expired ? 'expired' : 'active'}`}
            >
              {/* Status Badge */}
              <div className="story-status-badge">
                {story.is_expired ? (
                  <span className="badge-expired">⏰ Expirada</span>
                ) : (
                  <span className="badge-active">✅ Activa</span>
                )}
              </div>

              {/* Media Type Icon */}
              <div className="media-type-badge">
                {story.media_type === 'VIDEO' ? '🎥' : '🖼️'}
              </div>

              {/* Story Content */}
              <div className="story-content">
                <div className="story-header">
                  <span className="story-date">{formatDate(story.timestamp)}</span>
                  {!story.is_expired && (
                    <span className="time-remaining">{getTimeRemaining(story.expires_at)}</span>
                  )}
                </div>

                {/* Metrics */}
                <div className="story-metrics">
                  <div className="metric-row">
                    <div className="metric-item">
                      <span className="metric-icon">👁️</span>
                      <span className="metric-value">{formatNumber(story.impressions)}</span>
                      <span className="metric-label">Impresiones</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-icon">📡</span>
                      <span className="metric-value">{formatNumber(story.reach)}</span>
                      <span className="metric-label">Alcance</span>
                    </div>
                  </div>
                  <div className="metric-row">
                    <div className="metric-item">
                      <span className="metric-icon">💬</span>
                      <span className="metric-value">{formatNumber(story.replies)}</span>
                      <span className="metric-label">Respuestas</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-icon">👆</span>
                      <span className="metric-value">{formatNumber(story.taps_forward)}</span>
                      <span className="metric-label">Taps →</span>
                    </div>
                  </div>
                  <div className="metric-row">
                    <div className="metric-item">
                      <span className="metric-icon">👈</span>
                      <span className="metric-value">{formatNumber(story.taps_back)}</span>
                      <span className="metric-label">Taps ←</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-icon">🚪</span>
                      <span className="metric-value">{formatNumber(story.exits)}</span>
                      <span className="metric-label">Salidas</span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="story-footer">
                  <span className="fetch-count">
                    🔄 Actualizada {story.insights_fetch_count} veces
                  </span>
                  {story.permalink && (
                    <a
                      href={story.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-view-story"
                    >
                      Ver en Instagram ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InstagramStories;
